import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {FormControl, MenuItem, Select, SelectChangeEvent, Alert, Box} from '@mui/material'
import React from 'react'

interface CustomSelectProps {
  label: string
  value: string
  disabled?: boolean
  disabledPlaceholder?: string
  options: Array<{value: string; label: string}>
  onChange: (event: SelectChangeEvent<string>) => void
  showWarning?: boolean
  warningMessage?: string
  'data-test-id'?: string
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  disabled = false,
  disabledPlaceholder,
  options,
  onChange,
  showWarning = false,
  warningMessage = 'Changing this selection will reset your materials list.',
  'data-test-id': dataTestId
}) => {
  const hasValue = Boolean(value)
  const showFloatingLabel = disabled || hasValue

  return (
    <FormControl fullWidth sx={{mb: 1.5, minWidth: 0}}>
      <Select
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        data-test-id={dataTestId}
        MenuProps={{
          disablePortal: false,
          PaperProps: {
            sx: {
              maxHeight: 300,
              width: '100%',
              maxWidth: '100%',
              '& .MuiMenuItem-root': {
                whiteSpace: 'normal',
                wordWrap: 'break-word'
              }
            }
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          // Make menu same width as the select button
          style: {
            width: 'fit-content',
            maxWidth: '100%'
          }
        }}
        renderValue={(selected) => {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: showFloatingLabel ? '0px' : '4px',
                opacity: disabled ? 0.5 : 1,
                padding: '0px',
                width: '100%',
                minWidth: 0,
                overflow: 'hidden'
              }}
            >
              {/* Floating Label (shows when disabled or has value) */}
              {showFloatingLabel && (
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '16px',
                    color: '#52667a'
                  }}
                >
                  {label}
                </span>
              )}

              {/* Value or Label */}
              <span
                style={{
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '24px',
                  color: '#52667a',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {disabled && disabledPlaceholder
                  ? disabledPlaceholder
                  : selected
                    ? options.find((opt) => opt.value === selected)?.label || selected
                    : label}
              </span>
            </div>
          )
        }}
        sx={{
          height: '48px',
          minHeight: '48px',
          width: '100%',
          maxWidth: '100%',
          backgroundColor: disabled ? '#f5f7f9' : '#ffffff',
          borderRadius: '4px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d9e0',
            borderWidth: '1px'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: disabled ? '#d1d9e0' : '#016ad4'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#016ad4',
            borderWidth: '1px'
          },
          '&.Mui-disabled': {
            backgroundColor: '#f5f7f9',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d1d9e0'
            },
            '& .MuiSelect-icon': {
              opacity: 1,
              color: '#29333d',
              pointerEvents: 'none'
            }
          },
          '& .MuiSelect-select': {
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            minHeight: '48px',
            height: '48px',
            boxSizing: 'border-box'
          },
          '& .MuiSelect-icon': {
            opacity: 1,
            color: '#29333d',
            right: '8px'
          }
        }}
      >
        {/* Warning Alert */}
        {showWarning && (
          <Box sx={{px: 1, pb: 1, pt: 1, boxSizing: 'border-box'}}>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="small" />}
              sx={{
                fontFamily: 'Inter',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box',
                '& .MuiAlert-message': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              {warningMessage}
            </Alert>
          </Box>
        )}

        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              color: '#29333d',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              minHeight: '48px',
              paddingY: '12px'
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
